import type {
  WindowLayout,
  WorkspaceLayoutPreset,
  WorkspacePreset,
  WorkspacePresetsFieldsFragment,
} from 'generated/graphql';
import type { BrowserWindow } from 'common/WorkspaceManager/WorkspaceManager';
import type { ExtWindowName } from 'hooks/useExtensionState';

import { getSironaUrlFromScreenName } from 'utils/screenNames';
import { LISTENER_EVENTS } from './constants';

export const readCaseEvent = ({
  smid,
  target,
  workspacePreset,
  reporterOnly,
  patientJacketOnRead,
}: {
  smid: string;
  target: string;
  workspacePreset?: WorkspacePreset;
  reporterOnly?: boolean;
  patientJacketOnRead?: boolean;
}): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.READ_CASE, {
    detail: {
      smid,
      target,
      reporterOnly,
      patientJacketOnRead,
    },
  });
};

export const closeEvent = (): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.CLOSE);
};

export const clearCaseEvent = (): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.CLEAR_CASE);
};

export const requestWorkspaceLayoutEvent = (): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.REQUEST_WORKSPACE_LAYOUT);
};

export const refreshEvent = (): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.REFRESH);
};

export const initEvent = (): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.INIT);
};

export const logOutEvent = (): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.LOG_OUT, {
    detail: null,
    bubbles: true,
    composed: true,
  });
};

export const applyWorkspacePresetEvent = ({
  preset,
  caseId,
}: {
  preset:
    | any
    | {
        instanceId: string;
        windows: Array<Omit<BrowserWindow, 'tabs'>>;
      }
    | ({
        readonly __typename?: 'WorkspacePreset';
      } & WorkspacePresetsFieldsFragment);
  caseId: string;
}): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.APPLY_WORKSPACE_PRESET, {
    detail: {
      ...preset,
      // @ts-expect-error [EN-10263] - TS7006 - Parameter 'win' implicitly has an 'any' type.
      windows: preset.windows?.map((win) => ({
        ...win,
        url: getSironaUrlFromScreenName(win.screen, caseId),
      })),
    },
  });
};

export const applyWorkspaceLayoutPresetEvent = ({
  preset,
}: {
  preset: Pick<WorkspaceLayoutPreset, 'windows'> | { windows: ReadonlyArray<WindowLayout> };
}): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.APPLY_WORKSPACE_LAYOUT_PRESET, {
    detail: {
      windows: preset.windows,
    },
  });
};

export const openWindowEvent = ({
  url,
  windowConfig,
}: {
  url: string;
  windowConfig: {
    focused: Nullable<boolean>;
    top: Nullable<number>;
    left: Nullable<number>;
    width: Nullable<number>;
    height: Nullable<number>;
    state: string;
  };
}): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.OPEN_WINDOW, {
    detail: {
      url,
      target: url,
      config: windowConfig,
    },
  });
};

export const focusEvent = (): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.FOCUS);
};

export const focusAllEvent = (): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.FOCUS_ALL);
};

export const syncEvent = ({
  from,
  to,
  studies,
  focus = true,
}: {
  from: Nullable<string>;
  to: string;
  studies: Nullable<ReadonlyArray<string>>;
  focus?: boolean;
}): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.SYNC, {
    detail: {
      from,
      to,
      studies,
      focus,
    },
  });
};

export const extensionListenerReadyEvent = (): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.APP_READY);
};

export const openCaseEvent = ({
  smid,
  shouldReload,
}: {
  smid: string;
  shouldReload?: Nullable<boolean>;
}): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.SET_CASE_ID, {
    detail: {
      caseId: smid,
    },
  });
};

export const closeWindowEvent = ({ target }: { target: ExtWindowName }): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.CLOSE_WINDOW, {
    detail: {
      target,
    },
  });
};

export const transitionRouteEvent = ({
  target,
  path,
  focus,
}: {
  target: ExtWindowName;
  path: string;
  focus?: boolean;
}): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.TRANSITION_ROUTE, {
    detail: {
      target,
      path,
      focus: focus ?? false,
    },
  });
};

export const requestMemoryInfoEvent = (): CustomEvent => {
  return new CustomEvent(LISTENER_EVENTS.REQ_MEMORY_INFO);
};

export const addExtensionListener = (
  eventName: string,
  handler: EventListenerOrEventListenerObject
): void => {
  window.document.addEventListener(eventName, handler);
};

export const removeExtensionListener = (
  eventName: string,
  handler: EventListenerOrEventListenerObject
): void => {
  window.document.removeEventListener(eventName, handler);
};

export const dispatchExtensionEvent = (customEvent: CustomEvent): void => {
  window.document.dispatchEvent(customEvent);
};
